// Logger + App Insights init MUST be the first import (global error handler + AI init before render).
import { reactPlugin } from "./logger-init";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { SkyProvider } from "@skypointcloud/skypoint-ui";
import { BrowserRouter } from "react-router-dom";
import { withAITracking } from "@skypointcloud/logger/react";
import "@skypointcloud/skypoint-ui/styles.css";
import "./index.css";
import { App } from "./App";
import { SessionProvider } from "./lib/session";
import { AuthorizationGate } from "./auth/AuthorizationGate";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { isB2cMode } from "./auth/authMode";

// Wrap the app with App Insights render tracking when a UI resource is configured; otherwise render
// App directly (local dev with no VITE_APPINSIGHTS_UI_CONNECTION_STRING).
const Root = reactPlugin ? withAITracking(reactPlugin, App) : App;

const rootEl = document.getElementById("root")!;

// basic mode (DEFAULT): mount exactly as V1 — NO MsalProvider, NO MSAL import. SkyProvider (brand
// accent = Skypoint amber #FFB31C) wraps BrowserRouter; SessionProvider fetches /api/v1/me once so
// the shell can gate nav/UI by RBAC permissions. This path is unchanged from the basic-auth build.
function renderBasic() {
  createRoot(rootEl).render(
    <StrictMode>
      <ErrorBoundary variant="root" boundaryName="root-basic">
        <SkyProvider brandColor="#FFB31C">
          <BrowserRouter>
            <SessionProvider>
              <AuthorizationGate>
                <Root />
              </AuthorizationGate>
            </SessionProvider>
          </BrowserRouter>
        </SkyProvider>
      </ErrorBoundary>
    </StrictMode>,
  );
}

/**
 * Plain-DOM fallback when the b2c bootstrap CHUNK fails to load (a dynamic import() reject — common
 * right after a redeploy when the browser holds a stale hashed URL). No React is mounted yet, so an
 * ErrorBoundary cannot help. The reload uses an anchor (not an inline onclick) so the server CSP does
 * not block it; navigating to "/" re-fetches index.html with fresh chunk hashes.
 */
function renderChunkLoadFallback() {
  rootEl.innerHTML =
    '<div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui;background:#f6f7f9">' +
    '<div role="alert" style="max-width:460px;background:#fff;border:1px solid #e5e7eb;border-radius:12px;padding:32px;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,0.08)">' +
    '<h1 style="font-size:20px;color:#101828;margin:0 0 8px">Could not load skyCare</h1>' +
    '<p style="font-size:14px;color:#475467;line-height:1.5;margin:0 0 20px">A newer version may be available. Please reload to continue.</p>' +
    '<a href="/" style="display:inline-block;background:#242424;color:#fff;border-radius:8px;padding:10px 18px;font-size:14px;font-weight:600;text-decoration:none">Reload skyCare</a>' +
    "</div></div>";
}

// b2c mode (Phase 3 cutover): dynamically import the MSAL bootstrap so MSAL is never bundled or
// initialized in basic mode. The b2c shell adds MsalProvider + an auth gate + a /callback route
// around the same SessionProvider + App.
if (isB2cMode()) {
  import("./auth/B2cRoot")
    .then(({ renderB2c }) => renderB2c(rootEl, Root))
    .catch(() => renderChunkLoadFallback());
} else {
  renderBasic();
}
